import React, {useRef, useState, useEffect, useCallback} from "react";
import {useHistory, useParams} from "react-router-dom";
import {useSelector, useDispatch} from "react-redux";

import {
    Button,
    Col,
    FormGroup,
    Label,
    Row,
    Alert,
    Card,
    CardBody,
} from "reactstrap";
import {Button as PrimeReactButton} from 'primereact/button';
import {Message} from 'primereact/message';
import ReCAPTCHA from "react-google-recaptcha";

import "./proposal-v3.css";

// components
import Logo from "../../common/Logo";
import VerifyServiceInfo from "./VerifyServiceInfo";

// services
import * as newProposalApi from "../../services/newProposalApi";
import * as newProposalActions from "../../redux/actions/newProposalActions";
import {PROPOSAL_VIEW, PROPOSAL_STATUSES} from "../../services/appConstants";
import OptionalServices from "./OptionalServices";
import BuildingsTable from "./BuildingTable/BuildingTable";
import PricingSection from "./PricingSection/PricingSection";
import TextInput from "./TextInput";
import {formatSavedTime, getProposalRouteForYear, PROPOSAL_ROUTES} from "../../helpers/utils";

// Centralized status enum for save state
const SAVE_STATUS = Object.freeze({
    IDLE: 'idle',
    SAVING: 'saving',
    SUCCESS: 'success',
    ERROR: 'error',
});

const contractFiltersBtnCss = {
    minWidth: "70px",
    fontSize: "1rem",
    padding: "0.25rem 0.75rem",
};

const ProposalV3 = () => {
    let {id} = useParams();
    let history = useHistory();
    let dispatch = useDispatch();

    const {proposal, lines, expiringLines, underContractLines} = useSelector(
        (state) => state.newProposalReducer
    );
    const isFinalState = !!proposal && [
        PROPOSAL_STATUSES.SUBMITTED.value,
        PROPOSAL_STATUSES.ACCEPTED.value,
        PROPOSAL_STATUSES.CANCELLED.value,
        PROPOSAL_STATUSES.ARCHIVED.value,
        PROPOSAL_STATUSES.INACTIVE.value,
    ].includes(proposal.statusId);

    const logo = require("../../assets/img/vda_logo.svg");

    const [showLoader, setShowLoader] = useState(false);
    const [captchaValue, setCaptchaValue] = useState(false);
    const [errorMessage, setErrorMessage] = useState("error message section");
    const [billingEmailErrorMessage, setBillingEmailErrorMessage] = useState("");
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [view, setView] = useState(PROPOSAL_VIEW.EXPIRING);
    const [localPricing, setLocalPricing] = useState({
        cat1EscalatorFee: "",
        cat1ElevatorFee: "",
        cat1LiftFee: "",
        cat1FilingFee: "",
        cat5DeviceFees: "",
        cat5FilingFee: "",
        periodicElevatorFee: "",
        periodicFilingFee: "",
        dismantleElevatorFee: "",
        dismantleFilingFee: "",
        rescheduleFee: "",
        overtimeRate: "",
        fireServiceFee: "",
        aocFee: "",
        expeditingFee: "",
        qceFee: "",
    });

    // Save feedback state (see SAVE_STATUS)
    const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [showVerifyOptions, setShowVerifyOptions] = useState(false);

    // This is to track if the user has made changes to the form
    const [isDirty, setIsDirty] = useState(false);

    const markDirty = useCallback((dirty) => {
        setIsDirty(dirty);
        if (dirty) setSaveStatus(SAVE_STATUS.IDLE);
    }, [setIsDirty, setSaveStatus]);

    // This is the current state of the fields that were edited
    const [currentProposalState, setCurrentProposalState] = useState({
        auditDobNowEmail: "",
        agentSignature: "",
        verifyServiceOptIn: false,
        lines: [],
        billingContactEmail: "",
        billingContactName: "",
        newContractYear: null
    });

    useEffect(() => {
        if (!id) {
            history.push("/notFound");
        }
        loadProposal();
    }, []);

    useEffect(() => {
        const allowedStatuses = [
            PROPOSAL_STATUSES.DRAFT.value,
            PROPOSAL_STATUSES.SENT.value,
            PROPOSAL_STATUSES.VIEWED.value,
            PROPOSAL_STATUSES.MODIFIED.value,
        ];
        setShowVerifyOptions(allowedStatuses.includes(proposal?.statusId));
        setCurrentProposalState((prev) => ({
            ...prev,
            verifyServiceOptIn: proposal?.verifyServiceOptIn ?? false
        }));
    }, [proposal?.statusId]);

    const loadProposal = async () => {
        setShowLoader(true);

        if (!id) {
            history.push("/notFound");
            return;
        }

        const manifest = await newProposalApi.getNewManifestV2(id);

        if (manifest.status !== 200) {
            history.push("/notFound");
            return;
        }

        let proposalYear = manifest.data.proposal.year;
        const targetRoute = getProposalRouteForYear(proposalYear);
        if (targetRoute !== PROPOSAL_ROUTES.V3) {
            history.push(`${targetRoute}/${id}`);
            return;
        }


        setCurrentProposalState(prev => ({
            ...prev,
            newContractYear: proposalYear + 1,
            auditDobNowEmail: manifest.data.proposal.auditDobNowEmail || "",
            agentSignature: manifest.data.proposal.agentSignature || "",
            verifyServiceOptIn: manifest.data.proposal.verifyServiceOptIn || false,
            billingContactEmail: manifest.data.proposal.billingContactEmail || "",
            billingContactName: manifest.data.proposal.billingContactName || "",
        }));

        dispatch(newProposalActions.setNewProposal(manifest.data)).then((resp) => {
            setView(PROPOSAL_VIEW.EXPIRING);
            setShowLoader(false);
            setLocalPricing({
                cat1ElevatorFee: resp.proposal.renewalCat1ElevatorFee,
                cat1EscalatorFee: resp.proposal.renewalCat1EscalatorFee,
                cat1LiftFee: resp.proposal.renewalCat1LiftFee,
                cat1FilingFee: resp.proposal.renewalCat1FilingFee,

                cat5DeviceFees: resp.proposal.renewalCat5ElevatorFee,
                cat5FilingFee: resp.proposal.renewalCat5FilingFee,

                periodicElevatorFee: resp.proposal.renewalPeriodicElevatorFee,
                periodicEscalatorFee: resp.proposal.renewalPeriodicEscalatorFee,
                periodicLiftFee: resp.proposal.renewalPeriodicLiftFee,
                periodicFilingFee: resp.proposal.renewalPeriodicFilingFee,

                dismantleElevatorFee: resp.proposal.renewalDismantleElevatorFee,
                dismantleEscalatorFee: resp.proposal.renewalDismantleEscalatorFee,
                dismantleLiftFee: resp.proposal.renewalDismantleLiftFee,
                dismantleFilingFee: resp.proposal.renewalDismantleFilingFee,

                rescheduleFee: resp.proposal.renewalRescheduleFee,
                overtimeRate: resp.proposal.renewalOvertimeRate,
                fireServiceFee: resp.proposal.renewalFireServiceFee,

                aocFee: resp.proposal.renewalAocFee,
                expeditingFee: resp.proposal.renewalExpeditingFee,
                qceFee: resp.proposal.renewalQceFee,
                contractTerm: resp.proposal.contractTerm,
            });
        });
    };

    const captchaOptions = {
        siteKey: "6LceNHgtAAAAALqNNLlvePkQiMv0IMKv08yJJFJ4",
        captchaRef: useRef("test"),
    };

    const handleCaptcha = (captchaVal) => {
        setCaptchaValue(captchaVal ? true : false);
    };

    const validateForm = () => {
        if (!currentProposalState.auditDobNowEmail) {
            setErrorMessage("DOBNOW Email is required.");
            setShowErrorMessage(true);

            return false;
        }

        if (!validateEmail(currentProposalState.auditDobNowEmail)) {
            setErrorMessage("DOBNOW Email is not in a valid email form.");
            setShowErrorMessage(true);

            return false;
        }

        if (!currentProposalState.billingContactEmail) {
            setErrorMessage("Billing Contact Email is required.");
            setShowErrorMessage(true);

            return false;
        }

        if (!validateEmail(currentProposalState.billingContactEmail)) {
            setErrorMessage("Billing Contact Email is not in a valid email form.");
            setShowErrorMessage(true);

            return false;
        }

        if (!currentProposalState.billingContactName) {
            setErrorMessage("Billing Contact Name is required.");
            setShowErrorMessage(true);

            return false;
        }

        // make sure the status of the lines has something checked
        if (lines.some((c) => c.statusId == 0)) {
            setErrorMessage("Please Approve or Decline each of the buildings.");
            setShowErrorMessage(true);
            return false;
        }

        if (!currentProposalState.agentSignature) {
            setErrorMessage("You must sign the proposal.");
            setShowErrorMessage(true);
            return false;
        }

        //validate captcha
        if (!captchaValue) {
            setErrorMessage("Please verify that you are not a robot");
            setShowErrorMessage(true);
            return false;
        }

        setErrorMessage("");
        setShowErrorMessage(false);
        return true;
    };

    const handleSubmit = async () => {
        setShowErrorMessage(validateForm());
        if (validateForm()) {
            setShowLoader(true);

            let completeProposal = {...proposal};
            completeProposal.auditDobNowEmail = currentProposalState.auditDobNowEmail;
            completeProposal.agentSignature = currentProposalState.agentSignature;
            completeProposal.verifyServiceOptIn = currentProposalState.verifyServiceOptIn;
            completeProposal.billingContactEmail = currentProposalState.billingContactEmail;
            completeProposal.billingContactName = currentProposalState.billingContactName;
            completeProposal.statusId = PROPOSAL_STATUSES.SUBMITTED.value;

            let linesDataToUpdate = getProposalLinesFromBuildings(lines);

            // gather the proposal
            let proposalToSubmit = {
                proposal: completeProposal,
                lines: linesDataToUpdate,
            };

            try {
                await newProposalApi.submit(proposalToSubmit);
                setShowLoader(false);
                history.push("/Complete");
            } catch (_) {
                setShowLoader(false);
                setErrorMessage("The proposal could not be submitted.");
                setShowErrorMessage(true);
            }
        }
    };

    const getProposalLinesFromBuildings = (buildings) => {
        return buildings.flatMap(line =>
            line.devices.map(device => ({
                auditSuperName: line.auditSuperName || null,
                auditSuperPhone: line.auditSuperPhone || null,
                auditElevatorCompany: line.auditElevatorCompany || null,
                elevatorCompanyId: line.elevatorCompanyId || null,
                statusId: line.statusId ?? null,
                device: device.device // it is the device name
            }))
        );
    }

    const getLinesPropertiesForUpdate = (lines) => {
        // get only the fields we need to update
        return lines.map(line => ({
            device: line.device, // it is the device name
            auditSuperName: line.auditSuperName || null,
            auditSuperPhone: line.auditSuperPhone || null,
            auditElevatorCompany: line.auditElevatorCompany || null,
            elevatorCompanyId: line.elevatorCompanyId || null,
            statusId: line.statusId ?? null,
        }));
    }

    const validateEmail = (email) => /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);

    const handleSave = async () => {
        setBillingEmailErrorMessage("");

        if (isFinalState) {
            setSaveStatus(SAVE_STATUS.ERROR);
            setSaveError(new Error("Proposal is already in a final state. It cannot be updated."));
            return;
        }

        const emptyEmail = !currentProposalState.billingContactEmail;
        const validEmail = validateEmail(currentProposalState.billingContactEmail);
        if (!emptyEmail && !validEmail) {
            setBillingEmailErrorMessage("Billing contact email is not in a valid email form.");
            setSaveStatus(SAVE_STATUS.ERROR);
            setSaveError(new Error("Billing contact is required."));

            return;
        }

        try {
            setSaveStatus(SAVE_STATUS.SAVING);
            setSaveError(null);

            let linesDataToUpdate = getLinesPropertiesForUpdate(currentProposalState.lines);
            let payload = {...currentProposalState, lines: linesDataToUpdate};

            const resp = await newProposalApi.saveProposal(id, payload);

            const status = resp?.status ?? resp?.statusCode ?? 200;
            if (status == null || status < 200 || status >= 300) {
                const msg = resp?.data?.message || resp?.message || `Save failed (${status})`;
                throw new Error(msg);
            }

            markDirty(false);
            setLastSavedAt(new Date().toISOString());
            setSaveStatus(SAVE_STATUS.SUCCESS);
        } catch (err) {
            setSaveError(err);
            setSaveStatus(SAVE_STATUS.ERROR);
        }
    };

    const trackTouchedRow = useCallback((row) => {
        if (!row) return;
        const id = row.proposalDetailId;

        setCurrentProposalState((prev) => {
            const prevLines = prev.lines || [];

            if (id != null) {
                const idx = prevLines.findIndex((r) => r.proposalDetailId === id);
                if (idx !== -1) {
                    const updated = prevLines.slice();
                    updated[idx] = {...updated[idx], ...row};
                    return {...prev, lines: updated};
                }
            }
            // New entry
            return {...prev, lines: [...prevLines, row]};
        });

        markDirty(true);
    }, [markDirty]);

    return (
      <>
        {showLoader === true ? (
          <>
            <Row noGutters>
              <Col xl={12}>
                <Card className="mb-3">
                  <CardBody>
                    <Logo location={"auth"} logo={logo} />
                    <Row className="m-5">
                      <Col
                        xs="12"
                        style={{ alignSelf: "center", textAlign: "center" }}
                      >
                        <h3>
                          Preparing your proposal. One moment please......
                        </h3>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row noGutters>
              <Col xl={12}>
                <Card className="mb-3">
                  <CardBody>
                    <Logo location={"auth"} logo={logo} />
                    {proposal.statusId ===
                      PROPOSAL_STATUSES.SUBMITTED.value && (
                      <Alert color="success text-center">
                        <h3>Proposal Has Been Submitted!</h3>
                      </Alert>
                    )}
                    <Row className="m-2">
                      <Col>
                        <h5 className="text-center">
                          AFFIRMATION OF CORRECTION TRACKING, QUALITY CONTROL
                          EVALUATIONS AND FILING FEE PAYMENTS
                        </h5>
                      </Col>
                    </Row>
                    <Row className="m-2">
                      <Col>
                        <h5 className="text-center">
                          MANDATED WITNESSING OF CATEGORY 1/5 SAFETY INSPECTIONS
                          AND PERFORMANCE OF PERIODIC INSPECTIONS
                        </h5>
                      </Col>
                    </Row>
                    <Row className="m-2">
                      <Col>
                        <p className="text-left">
                          VDA is pleased to provide this extension agreement for
                          your annual vertical transportation inspections. This
                          agreement includes the following services and is
                          subject to terms and conditions which can be viewed by
                          clicking this link  <a
                                href="https://vdassoc.com/terms-of-sale/"
                                target="_blank"
                                style={{ textDecoration: "none" }}
                              >
                                <b>Terms of Sale | Vertical Transportation Services | VDA</b>
                              </a>:
                        </p>
                      </Col>
                    </Row>

                    <Row className="m-2">
                      <Col>
                        <ul>
                          <li>Mandated Services</li>
                          <ul>
                            <li>
                              Third Party Witness for Category 1 and Category 5
                              Inspections. Please note NYC Department of
                              Buildings (NYC DOB) regulations state that there
                              must be a six (6) month interval between annual
                              Category 1 Inspections.{" "}
                            </li>
                            <li>
                              Third Party Performance of Periodic Inspections:
                              NYC DOB regulations state that there must be a
                              ninety (90) day interval between Periodic
                              Inspections and Category 1 Inspections.{" "}
                            </li>
                          </ul>
                          <li>Optional Services</li>
                          <ul>
                            <li>
                              Affirmation of Corrections Tracking (AOC): All
                              inspections that report unsatisfactory results
                              must be corrected and filed with NYC DOB within
                              certain timeframes. AOC’s will be tracked for a
                              period of one hundred ninety-four (194) days
                              following the initial inspection/witness date.
                              This is consistent with the NYC DOB maximum
                              allotted time for corrections should extensions be
                              granted. Our compliance department will make every
                              effort possible to work with the elevator company
                              and building agent/owner to ensure that
                              corrections are completed and filed within the
                              allotted timeframes mandated by NYC DOB. VDA will
                              not be responsible for late filings or penalties
                              incurred because of late filings or failure to
                              file.
                            </li>
                            <li>
                              QCEs are performed alongside your scheduled
                              inspection service visits. VDA provides a detailed
                              written report evaluating your elevator
                              maintenance against industry standards. The report
                              includes an overview of maintenance deficiencies,
                              current operating performance times, known and
                              anticipated code-mandated upgrades or alterations,
                              a system equipment profile, a preliminary
                              violation search, and both short- and long-term
                              recommendations. If the quarterly QCE option is
                              selected, some evaluations will be scheduled
                              independently from the category and periodic
                              inspections.
                            </li>
                            <li>
                              Expediting DOB Filing Fees: NYC DOB filing fees
                              are the responsibility of the Building
                              Owner/Manager. Upon completion and attestation
                              from all related parties, VDA will process the
                              payment on your behalf (if the device is free from
                              any elevator related penalty and/or fine). The DOB
                              Filing fee will be passed through with an
                              additional expediting fee for each filing fee
                              paid.
                            </li>
                          </ul>
                        </ul>
                      </Col>
                    </Row>

                    <Row className="m-2">
                      <Col>
                        <p className="text-left">
                          <u>
                            <b>
                              Below is a list of buildings/devices/inspection
                              types that are on file with our company (devices
                              not listed will not be included in our services.
                              Owner/Agent must notify VDA of any additional
                              devices prior to scheduling to meet all NYC DOB
                              deadlines. VDA will not be responsible for any
                              fines/penalties incurred for devices not
                              listed/approved). Please review the list and edit
                              as necessary with the approve, decline, or edit
                              functions.{" "}
                            </b>
                          </u>
                          <br />
                          <br />
                          You can add or remove options in the 'Optional
                          Services' section by selecting the relevant locations
                          and additional services, then clicking 'Update the
                          selected locations.'
                        </p>
                         <p className="text-left">
                          To update the elevator maintenance company or onsite
                          contact information for a building, scroll down to the
                          'Buildings List' section, click the 'Edit' button,
                          make the necessary changes to the desired fields, and
                          then click 'Save'.
                        </p>
                         <p className="text-left">
                          To ensure timely scheduling and compliance, please
                          make sure you have approved and returned this proposal
                          as soon as possible as well as any proposals submitted
                          by your elevator maintenance contractor. This will
                          enable us to schedule accordingly and within the NYC
                          DOB rules. VDA is not responsible for notifying the
                          building/super/resident manager of scheduled tests
                          date(s).
                        </p>
                      </Col>
                    </Row>
                    {showErrorMessage == true && (
                      <Alert color="danger">{errorMessage}</Alert>
                    )}

                    <Row>
                      <Col xs="12" style={{ alignSelf: "center" }}>
                        <Alert color="info">
                          <FormGroup>
                            <Label>
                              DOBNOW Email -{" "}
                              <b>
                                PLEASE CONFIRM DOBNOW EMAIL BELOW AND
                                EDIT/PROVIDE ACCORDINGLY
                              </b>
                            </Label>
                            <TextInput
                              disabled={!proposal?.canEdit}
                              placeholder="DOBNOW Email"
                              initialValue={proposal?.auditDobNowEmail}
                              updateParent={(email) => {
                                setCurrentProposalState((prev) => ({
                                  ...prev,
                                  auditDobNowEmail: email,
                                }));
                                markDirty(true);
                              }}
                            />
                          </FormGroup>
                        </Alert>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="12" style={{ alignSelf: "center" }}>
                        <Alert color="info">
                          <h5>Billing Contact information</h5>
                          <FormGroup>
                            <Row>
                              <Col>
                                <TextInput
                                  label="Billing Contact name"
                                  disabled={!proposal?.canEdit}
                                  initialValue={proposal?.billingContactName}
                                  updateParent={(name) => {
                                    setCurrentProposalState((prev) => ({
                                      ...prev,
                                      billingContactName: name,
                                    }));
                                    markDirty(true);
                                  }}
                                />
                              </Col>
                              <Col>
                                <TextInput
                                  label="Billing Contact email"
                                  disabled={!proposal?.canEdit}
                                  initialValue={proposal?.billingContactEmail}
                                  updateParent={(email) => {
                                    setCurrentProposalState((prev) => ({
                                      ...prev,
                                      billingContactEmail: email,
                                    }));
                                    markDirty(true);
                                  }}
                                  error={billingEmailErrorMessage}
                                />
                              </Col>
                            </Row>
                          </FormGroup>
                        </Alert>
                      </Col>
                    </Row>
                    <Row>
                      <Col xl={4}>
                        <div className="text-center">
                          <Label>Customer</Label>
                          <h5>
                            {proposal && proposal.customerName
                              ? proposal.customerName
                              : ""}
                          </h5>
                        </div>
                      </Col>
                      <Col xl={4}>
                        <div className="text-center">
                          <Label>Agent</Label>
                          <h5>
                            {proposal && proposal.agentName
                              ? proposal.agentName
                              : ""}
                          </h5>
                        </div>
                      </Col>
                      <Col xl={4}>
                        <div className="text-center">
                          <Label>Email</Label>
                          <h5>
                            {proposal && proposal.agentEmail
                              ? proposal.agentEmail
                              : ""}
                          </h5>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row noGutters>
              <Col xl={12}>
                <Card className="mb-3 text-center">
                  <CardBody>
                    {view === PROPOSAL_VIEW.EXPIRING && (
                      <PricingSection
                        localPricing={localPricing}
                        proposalYear={currentProposalState.newContractYear}
                      />
                    )}
                    <Row noGutters className="mt-3">
                      <Col xl={12}>
                        <PrimeReactButton
                          className="fs-1 mr-2 text-400 text-white rounded"
                          size={"small"}
                          style={contractFiltersBtnCss}
                          label={`Approval Required (${expiringLines.length})`}
                          severity={
                            view === PROPOSAL_VIEW.EXPIRING
                              ? "primary"
                              : "secondary"
                          }
                          onClick={() => setView(PROPOSAL_VIEW.EXPIRING)}
                        />
                        <PrimeReactButton
                          className="fs-1 mr-2 text-400 text-white rounded"
                          style={contractFiltersBtnCss}
                          label={`Under Contract (${underContractLines.length})`}
                          severity={
                            view === PROPOSAL_VIEW.CURRENT
                              ? "primary"
                              : "secondary"
                          }
                          onClick={() => setView(PROPOSAL_VIEW.CURRENT)}
                        />
                        <PrimeReactButton
                          className="fs-1 mr-2 text-400 text-white rounded"
                          style={contractFiltersBtnCss}
                          label={`View All (${lines.length})`}
                          severity={
                            view === PROPOSAL_VIEW.ALL ? "primary" : "secondary"
                          }
                          onClick={() => setView(PROPOSAL_VIEW.ALL)}
                        />
                      </Col>
                    </Row>
                    <OptionalServices view={view} />
                    <BuildingsTable
                      view={view}
                      onRowTouched={trackTouchedRow}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row noGutters>
              <Col xl={12}>
                <Card className="mb-3">
                  <CardBody>
                    <Row>
                      <Col xs="12" style={{ alignSelf: "center" }}>
                        <VerifyServiceInfo
                          showVerifyOptions={showVerifyOptions}
                          verifyServiceOptIn={
                            currentProposalState.verifyServiceOptIn
                          }
                          setVerifyServiceOptIn={(val) => {
                            setCurrentProposalState((prev) => ({
                              ...prev,
                              verifyServiceOptIn: val,
                            }));

                            markDirty(true);
                          }}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs="6" style={{ alignSelf: "center" }}>
                        <Alert color="info">
                          <FormGroup>
                            <span>Signature</span>
                            <TextInput
                              disabled={!proposal?.canEdit}
                              placeholder="Agent Signature"
                              initialValue={proposal?.agentSignature}
                              updateParent={(signature) => {
                                setCurrentProposalState((prev) => ({
                                  ...prev,
                                  agentSignature: signature,
                                }));
                                markDirty(true);
                              }}
                            />
                            <Label style={{ display: "flex", gap: "4px" }}>
                              By Signing above, I agree to
                              <a
                                href="https://vdassoc.com/terms-of-sale/"
                                target="_blank"
                                style={{ textDecoration: "none" }}
                              >
                                <b>VDA, Inc's Terms & Conditions</b>
                              </a>
                            </Label>
                          </FormGroup>
                        </Alert>
                      </Col>
                      <Col xs="6" style={{ alignSelf: "left" }}></Col>
                    </Row>

                    {proposal?.canEdit === true && (
                      <>
                        <Row>
                          <Col xs="12" style={{ alignSelf: "center" }}>
                            <Row className="mt-3 justify-content-center mb-3">
                              <Col xs="auto">
                                <ReCAPTCHA
                                  ref={captchaOptions.captchaRef}
                                  sitekey={captchaOptions.siteKey}
                                  onChange={handleCaptcha}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs="12" style={{ alignSelf: "center" }}>
                            {showErrorMessage && (
                              <Alert color="danger">{errorMessage}</Alert>
                            )}
                            <Row className="mt-3 justify-content-center mb-3">
                              <Col xs="auto">
                                <Button
                                  type="submit"
                                  className="btn-success btn-lg"
                                  onClick={handleSubmit}
                                >
                                  Submit
                                </Button>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </>
                    )}

                    {proposal.statusId ===
                      PROPOSAL_STATUSES.SUBMITTED.value && (
                      <Alert color="success text-center">
                        <h3>Proposal Has Been Submitted!</h3>
                      </Alert>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {proposal?.canEdit && !isFinalState && (
              <div className="floating-save">
                {saveStatus === SAVE_STATUS.SAVING && (
                  <Message severity="info" text="Saving..." />
                )}

                {saveStatus === SAVE_STATUS.SUCCESS && !isDirty && (
                  <Message
                    severity="success"
                    text={`Saved • ${formatSavedTime(lastSavedAt)}`}
                  />
                )}

                {saveStatus === SAVE_STATUS.ERROR && (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Message severity="error" text="Save failed — Retry" />
                    <PrimeReactButton
                      size="small"
                      label="Retry"
                      severity="danger"
                      onClick={handleSave}
                    />
                  </div>
                )}

                {isDirty &&
                  saveStatus !== SAVE_STATUS.SAVING &&
                  saveStatus !== SAVE_STATUS.ERROR && (
                    <PrimeReactButton
                      label="Save"
                      severity="success"
                      icon="pi pi-save"
                      className="text-white"
                      onClick={handleSave}
                    />
                  )}
              </div>
            )}
          </>
        )}
      </>
    );
};

export default ProposalV3;